import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { MapPin, Plus, X, Loader2, Check } from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// French postal code API
const getCityFromPostalCode = async (postalCode) => {
  if (!postalCode || postalCode.length !== 5) return null;
  
  try {
    const response = await fetch(
      `https://geo.api.gouv.fr/communes?codePostal=${postalCode}&fields=nom,centre,codesPostaux&limit=10`
    );
    
    if (!response.ok) throw new Error("API error");
    
    const data = await response.json();
    
    if (data && data.length > 0) {
      return data.map(commune => ({
        city: commune.nom,
        postal_code: postalCode,
        lat: commune.centre?.coordinates?.[1] || null,
        lng: commune.centre?.coordinates?.[0] || null
      }));
    }
    
    return null;
  } catch (error) {
    console.error("Error fetching city from postal code:", error);
    return null;
  }
};

// Search cities by name
const searchCities = async (query) => {
  if (!query || query.length < 2) return [];
  
  try {
    const response = await fetch(
      `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(query)}&fields=nom,centre,codesPostaux&limit=10`
    );
    
    if (!response.ok) throw new Error("API error");
    
    const data = await response.json();
    
    return data.map(commune => ({
      city: commune.nom,
      postal_code: commune.codesPostaux?.[0] || "",
      lat: commune.centre?.coordinates?.[1] || null,
      lng: commune.centre?.coordinates?.[0] || null
    }));
  } catch (error) {
    console.error("Error searching cities:", error);
    return [];
  }
};

const ZoneSelector = ({ zones, onChange }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [radius, setRadius] = useState(10);
  const searchTimeout = useRef(null);
  const containerRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search as user types
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (!searchQuery || searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsSearching(true);

    searchTimeout.current = setTimeout(async () => {
      // Check if it's a postal code (5 digits)
      if (/^\d{5}$/.test(searchQuery)) {
        const results = await getCityFromPostalCode(searchQuery);
        setSuggestions(results || []);
      } else {
        // Search by city name
        const results = await searchCities(searchQuery);
        setSuggestions(results);
      }
      setIsSearching(false);
      setShowSuggestions(true);
    }, 300);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchQuery]);

  const handleSelectSuggestion = (suggestion) => {
    setSelectedZone(suggestion);
    setSearchQuery(`${suggestion.city} (${suggestion.postal_code})`);
    setShowSuggestions(false);
  };

  const handleAddZone = () => {
    if (!selectedZone) return;

    // Check if zone already exists
    const exists = zones.some(
      z => z.postal_code === selectedZone.postal_code && z.city === selectedZone.city
    );

    if (exists) {
      return;
    }

    const newZone = {
      city: selectedZone.city,
      postal_code: selectedZone.postal_code,
      radius: radius,
      lat: selectedZone.lat,
      lng: selectedZone.lng
    };

    onChange([...zones, newZone]);
    setSearchQuery("");
    setSelectedZone(null);
    setRadius(10);
    setSuggestions([]);
  };

  const handleRemoveZone = (index) => {
    const newZones = [...zones];
    newZones.splice(index, 1);
    onChange(newZones);
  };

  const handleUpdateRadius = (index, newRadius) => {
    const newZones = [...zones];
    newZones[index] = { ...newZones[index], radius: newRadius };
    onChange(newZones);
  };

  return (
    <div className="space-y-4">
      {/* Search input */}
      <div className="bg-[#0D1C32] rounded-xl p-4 border border-[#2A3A4D]" ref={containerRef}>
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] pointer-events-none z-10" />
            <Input
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedZone(null);
              }}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              className="input-dark pl-10"
              placeholder="Saisissez un code postal ou une ville..."
              data-testid="zone-search-input"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] animate-spin" />
            )}
            {selectedZone && (
              <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00E073]" />
            )}
          </div>
          <Button 
            type="button" 
            onClick={handleAddZone}
            className="btn-primary"
            disabled={!selectedZone}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 mt-1 w-full max-w-md bg-[#1F2A38] border border-[#2A3A4D] rounded-xl shadow-xl overflow-hidden">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSelectSuggestion(suggestion)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#2A3A4D] transition-colors border-b border-[#2A3A4D] last:border-b-0"
              >
                <MapPin className="w-4 h-4 text-[#00E073] flex-shrink-0" />
                <div>
                  <span className="text-white font-medium">{suggestion.city}</span>
                  <span className="text-[#94A3B8] ml-2">({suggestion.postal_code})</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Radius slider */}
        {selectedZone && (
          <div className="mt-4 pt-4 border-t border-[#2A3A4D]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#94A3B8]">Rayon d'intervention autour de {selectedZone.city}</span>
              <span className="text-sm font-medium text-[#00E073]">{radius} km</span>
            </div>
            <Slider
              value={[radius]}
              onValueChange={(value) => setRadius(value[0])}
              max={100}
              min={1}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-[#64748B] mt-1">
              <span>1 km</span>
              <span>100 km</span>
            </div>
          </div>
        )}

        <p className="text-xs text-[#64748B] mt-3">
          Saisissez un code postal (ex: 33000) ou un nom de ville pour voir les suggestions
        </p>
      </div>

      {/* Selected zones list */}
      {zones.length > 0 && (
        <div className="space-y-3">
          {zones.map((zone, index) => (
            <div 
              key={index}
              className="bg-[#1F2A38] rounded-xl p-4 border border-[#2A3A4D]"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#00E073]" />
                  <span className="font-medium text-white">{zone.city}</span>
                  <span className="text-sm text-[#64748B]">({zone.postal_code})</span>
                </div>
                <button 
                  type="button"
                  onClick={() => handleRemoveZone(index)}
                  className="text-[#94A3B8] hover:text-red-400 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[#94A3B8]">Rayon</span>
                <span className="text-sm font-medium text-[#00E073]">{zone.radius} km</span>
              </div>
              <Slider
                value={[zone.radius]}
                onValueChange={(value) => handleUpdateRadius(index, value[0])}
                max={100}
                min={1}
                step={1}
                className="w-full"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ZoneSelector;
